import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { FaBell, FaSearch, FaUser } from 'react-icons/fa';
import Logo from '../../Logo.jsx';
import { useNotificationsContext } from '../../../contexts/NotificationsContext.jsx';
import { useLocale } from '../../../contexts/LocaleContext.jsx';
import { useBroadcastGuard } from '../../../contexts/BroadcastSurface.jsx';
import { unreadBadgeLabel } from '../../../_helpers/notifications.js';
import { isSignedIn, rememberReturnPath } from '../../../_helpers/safeRedirect.js';
import { useFetchWrapper } from '../../../_helpers/fetchWrapper.js';
import { resolveMediaUrl } from '../../../hooks/useStreamBootstrap.js';
import config from '../../../config.js';
import AccountMenu from './AccountMenu.jsx';
import NotificationsDropdown from './NotificationsDropdown.jsx';
import '../../../styles/StreamPageHeader.css';

/**
 * The profile picture the OAuth callback left in the session, or null when
 * there is none. Older builds stored the strings "null" and "undefined", which
 * resolveMediaUrl would turn into a broken `<media>/null` URL (ENG-764).
 */
const storedProfileImage = () => {
  const value = sessionStorage.getItem('ProfileImageURL');
  if (!value || value === 'null' || value === 'undefined') return null;
  return value;
};

/**
 * The stream page header (mockup 01, ENG-667): logo, Discovery Page,
 * Memories and Shop (ENG-864) links, UPGRADE, the performer/music search, the notification bell
 * with an unread dot, and the viewer's avatar.
 *
 * With `onSearchChange`, the search is the stream page's own (ENG-701): the
 * page shows results as the viewer types and Esc clears it; Enter still goes
 * to the Discovery Page.
 *
 * Nothing displays over a broadcast (ENG-840): while a page shows one (the
 * stream page always, the home page with a live card), the bell's and the
 * avatar's menus open inline, in the page flow under the header, and push the
 * player down instead of hanging over it. Elsewhere they float as before.
 */
const StreamPageHeader = ({ searchTerm, onSearchChange, searchInputRef } = {}) => {
  const { t } = useLocale();
  const navigate = useNavigate();
  const [ownTerm, setOwnTerm] = useState('');
  const controlled = typeof onSearchChange === 'function';
  const term = controlled ? searchTerm ?? '' : ownTerm;
  const setTerm = controlled ? onSearchChange : setOwnTerm;
  const { unread } = useNotificationsContext();
  // The bell's dropdown (ENG-725) or the avatar's menu (ENG-723), one at a time.
  const [openMenu, setOpenMenu] = useState(null);
  const bellRef = useRef(null);
  const avatarRef = useRef(null);
  const openMenuRef = useRef(null);
  openMenuRef.current = openMenu;
  // Esc hands focus back to the trigger that opened the menu.
  const closeMenu = useCallback((reason) => {
    const trigger = openMenuRef.current === 'notifications' ? bellRef.current : avatarRef.current;
    setOpenMenu(null);
    if (reason === 'escape') trigger?.focus();
  }, []);
  const toggle = (menu) => setOpenMenu((current) => (current === menu ? null : menu));
  const inline = useBroadcastGuard().surfaces > 0;
  const uid = useId().replace(/:/g, '');
  const notificationsId = `stream-page-notifications-${uid}`;
  const accountId = `stream-page-account-${uid}`;
  const signedIn = isSignedIn();
  // The avatar comes from `GET /me` (ENG-764), the same source as the open
  // account menu; the session's copy only covers the moment before it answers.
  const fetchWrapperRef = useRef(useFetchWrapper());
  const [me, setMe] = useState(null);
  // The URL that failed to load, so a new URL gets its own chance.
  const [failedAvatar, setFailedAvatar] = useState(null);

  useEffect(() => {
    if (!signedIn) return undefined;
    let cancelled = false;
    fetchWrapperRef.current
      .get(`${config.apiBaseUrl}/me`)
      .then((answer) => {
        if (cancelled || !answer) return;
        setMe(answer);
        // Keep the other headers that read the session's copy current.
        if (answer.avatarUrl) sessionStorage.setItem('ProfileImageURL', answer.avatarUrl);
        else sessionStorage.removeItem('ProfileImageURL');
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [signedIn]);

  const avatar = signedIn ? resolveMediaUrl(me ? me.avatarUrl : storedProfileImage()) : null;
  const showImage = Boolean(avatar) && avatar !== failedAvatar;

  const handleSearch = (event) => {
    event.preventDefault();
    const trimmed = term.trim();
    navigate(trimmed ? `/performers?name=${encodeURIComponent(trimmed)}` : '/performers');
  };

  const signIn = () => {
    rememberReturnPath();
    navigate('/login');
  };

  const menu = !signedIn ? null
    : openMenu === 'notifications' ? <NotificationsDropdown onClose={closeMenu} inline={inline} id={notificationsId} />
      : openMenu === 'account' ? <AccountMenu onClose={closeMenu} inline={inline} id={accountId} />
        : null;

  const header = (
    <header className="stream-page-top" data-testid="stream-page-header">
      <div className="stream-page-top-start">
        <Logo size="small" />
        <nav className="stream-page-top-links" aria-label={t('navigation.main')}>
          <NavLink to="/performers" className="stream-page-top-link">{t('stream.header.discovery')}</NavLink>
          <NavLink to="/history" className="stream-page-top-link">{t('stream.header.memories')}</NavLink>
          <NavLink to="/shop" className="stream-page-top-link">{t('stream.header.shop')}</NavLink>
        </nav>
      </div>
      <div className="stream-page-top-end">
        <NavLink to="/upgrade" className="stream-page-upgrade">{t('stream.header.upgrade')}</NavLink>
        <div className="stream-page-header-actions" data-testid="stream-page-header-actions">
          <form className="stream-page-search" role="search" onSubmit={handleSearch}>
            <FaSearch className="stream-page-search-icon" aria-hidden="true" />
            <input
              ref={searchInputRef}
              type="search"
              className="stream-page-search-input"
              placeholder={t('stream.header.searchPlaceholder')}
              aria-label={t('stream.header.searchLabel')}
              value={term}
              onChange={(event) => setTerm(event.target.value)}
              onKeyDown={(event) => { if (event.key === 'Escape') setTerm(''); }}
            />
          </form>
          <button
            type="button"
            className="stream-page-bell"
            aria-label={unreadBadgeLabel(unread, t)}
            onClick={() => (signedIn ? toggle('notifications') : navigate('/notifications'))}
            aria-expanded={openMenu === 'notifications'}
            aria-controls={signedIn && openMenu === 'notifications' ? notificationsId : undefined}
            ref={bellRef}
            data-notifications-trigger
            data-testid="stream-page-bell"
          >
            <FaBell aria-hidden="true" />
            {unread > 0 && <span className="stream-page-bell-dot" data-testid="stream-page-bell-dot" aria-hidden="true" />}
          </button>
          {signedIn ? (
            <button
              type="button"
              className="stream-page-avatar"
              onClick={() => toggle('account')}
              aria-label={t('stream.header.account')}
              aria-expanded={openMenu === 'account'}
              aria-controls={openMenu === 'account' ? accountId : undefined}
              ref={avatarRef}
              data-account-trigger
              data-testid="stream-page-avatar"
            >
              {showImage
                ? <img src={avatar} alt="" onError={() => setFailedAvatar(avatar)} />
                : me?.initials
                  ? <span className="stream-page-avatar-initials" aria-hidden="true">{me.initials}</span>
                  : <FaUser aria-hidden="true" />}
            </button>
          ) : (
            <button type="button" className="stream-page-signin" onClick={signIn}>
              {t('common.actions.signIn')}
            </button>
          )}
          {!inline && menu}
        </div>
      </div>
    </header>
  );

  if (!inline) return header;
  return (
    <>
      {header}
      {menu && (
        <div className="stream-page-menu-panel" data-testid="stream-page-menu-panel">
          {menu}
        </div>
      )}
    </>
  );
};

export default StreamPageHeader;
